//Frontend/src/unicodex_ui/components/ManufacturerNav.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../styles/ManufacturerPortal.css";
import { clearPortalSession, getStoredPermissions, getStoredPortalUser } from "../services/authApi";

function ManufacturerNav({ title, subtitle, onSignOut, links: linksProp }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const perms = getStoredPermissions();
  const user = getStoredPortalUser();
  const touchRef = useRef({ x: 0, y: 0, swiping: false });

  const links = useMemo(
    () => {
      const fallback = [];

      // Manufacturer admin legacy pages (no perm gating assumed)
      if (user?.role === "manufacturer_admin") {
        fallback.push(
          { to: "/portal/dashboard", label: "Overview" },
          { to: "/portal/products", label: "Products" },
          { to: "/portal/batches", label: "Batches" },
          { to: "/portal/codes", label: "Codes" }
        );
      } else {
        // Role users: permission-gated modules
        fallback.push(
          { to: "/portal/dashboard", label: "Dashboard", perm: "portal.dashboard.read" },
          { to: "/portal/pos-inventory", label: "POS Inventory", perm: "pos.inventory.submit.read" },
          { to: "/portal/inventory", label: "Inventory", perm: "inventory.stock.levels.read" },
          { to: "/portal/purchasing", label: "Purchasing", perm: "purchasing.po.read" },
          { to: "/portal/accounting", label: "Accounting", perm: "accounting.read" },
          { to: "/portal/metered", label: "Metered", perm: "meter.session.read" },
          { to: "/portal/forecast", label: "Forecast", perm: "analytics.forecast.read" },
          { to: "/portal/compliance", label: "Compliance", perm: "compliance.read" },
        );
      }

      const raw = Array.isArray(linksProp) && linksProp.length ? linksProp : fallback;
      // Optional permission gating per link: { perm?: "purchasing.po.read" }
      return raw.filter((l) => !l.perm || perms.includes(l.perm));
    },
    [linksProp, perms, user?.role]
  );

  function toggle() {
    setOpen((v) => !v);
  }

  function close() {
    setOpen(false);
  }

  function signOut() {
    close();
    if (onSignOut) onSignOut();
    else {
      clearPortalSession();
      navigate("/portal/login", { replace: true });
    }
  }

  // auto-close menu on navigation
  useEffect(() => {
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while drawer is open (mobile)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Swipe-to-close (drawer opens from right; swipe right closes)
  function onDrawerTouchStart(e) {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, swiping: true };
  }

  function onDrawerTouchMove(e) {
    if (!touchRef.current.swiping) return;
    const t = e.touches && e.touches[0];
    if (!t) return;

    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;

    // If user is clearly scrolling vertically, ignore swipe-close detection.
    if (Math.abs(dy) > 18 && Math.abs(dy) > Math.abs(dx)) {
      touchRef.current.swiping = false;
      return;
    }

    // Swipe right to close (threshold)
    if (dx > 80) {
      touchRef.current.swiping = false;
      close();
    }
  }

  function onDrawerTouchEnd() {
    touchRef.current.swiping = false;
  }

  return (
    <>
      <div className="ucx-mfg-topbar">
        <div className="ucx-mfg-topbar-inner">
          <div className="ucx-mfg-brand" onClick={() => navigate("/portal/dashboard")} role="button" tabIndex={0}>
          <div className="ucx-mfg-brand-title">{title || "Portal"}</div>
          {subtitle ? <div className="ucx-mfg-brand-sub">{subtitle}</div> : null}
          {user?.role ? <div className="ucx-mfg-brand-sub">Role: {user.role}</div> : null}
          </div>

        {/* Desktop nav (scrolls horizontally if links overflow) */}
          <nav className="ucx-mfg-navlinks ucx-mfg-navlinks--desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={close}
              className={({ isActive }) => (isActive ? "ucx-mfg-link active" : "ucx-mfg-link")}
            >
              {l.label}
            </NavLink>
          ))}
          </nav>

          {/* Desktop actions (right side) */}
          {user ? (
            <div className="ucx-mfg-topbar-actions ucx-mfg-actions--desktop">
              <button type="button" className="ucx-btn ucx-btn-outline ucx-mfg-signout" onClick={signOut}>
                Logout
              </button>
            </div>
          ) : null}

          {/* Mobile hamburger (right side) */}
          <button
            type="button"
            className={`ucx-mfg-burger ${open ? "is-open" : ""}`}
            onClick={toggle}
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open ? "true" : "false"}
            aria-controls="ucx-mfg-drawer"
          >
            <span className="ucx-mfg-burger-bar" />
            <span className="ucx-mfg-burger-bar" />
            <span className="ucx-mfg-burger-bar" />
          </button>

        </div>
      </div>

      {/* Backdrop (kept mounted so it can animate out) */}
      <button
        type="button"
        className={`ucx-mfg-backdrop ${open ? "is-open" : ""}`}
        aria-label="Close menu"
        aria-hidden={open ? "false" : "true"}
        tabIndex={open ? 0 : -1}
        onClick={close}
      />

      {/* Mobile drawer */}
      <nav
        id="ucx-mfg-drawer"
        className={`ucx-mfg-drawer ${open ? "is-open" : ""}`}
        aria-label="Manufacturer navigation (mobile)"
        onTouchStart={onDrawerTouchStart}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
      >
        <div className="ucx-mfg-drawer-head">
          <div className="ucx-mfg-drawer-title">Menu</div>
          <button type="button" className="ucx-mfg-drawer-close" aria-label="Close" onClick={close}>
            ✕
          </button>
        </div>

        <div className="ucx-mfg-navlinks ucx-mfg-navlinks--drawer">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={close}
              className={({ isActive }) => (isActive ? "ucx-mfg-link active" : "ucx-mfg-link")}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="ucx-mfg-topbar-actions ucx-mfg-actions--drawer">
          <button type="button" className="ucx-btn ucx-btn-outline ucx-mfg-signout" onClick={signOut}>
            Logout
          </button>
        </div>
      </nav>
    </>
  );
}

export default ManufacturerNav;
